import { Injectable } from "@angular/core";
import { Observable, Subject, Subscription, timer } from "rxjs";
import { take, timeout } from "rxjs/operators";
import { ClientService } from "./client.service";
import { PusherService } from "./pusher.service";

export type DemandWaitTransitionType = "devis" | "canceled" | "problem" | "meeting";

export interface DemandWaitTransition {
  type: DemandWaitTransitionType;
  devisId?: number;
  demandeId?: number;
  meetings?: unknown[];
}

const WAIT_TIMEOUT_MS = 5 * 60_000;

@Injectable({
  providedIn: "root",
})
export class DemandDevisWaitService {
  private activeDemandId: number | null = null;
  private subscriptions: Subscription[] = [];
  private transitionSubject: Subject<DemandWaitTransition> | null = null;

  constructor(
    private clientService: ClientService,
    private pusherService: PusherService
  ) {}

  waitForDevisTransition(demandId: number): Observable<DemandWaitTransition> {
    this.stopWait();
    this.activeDemandId = demandId;
    this.transitionSubject = new Subject<DemandWaitTransition>();
    this.bootstrapFromApi(demandId);
    this.listenToRealtime(demandId);

    return this.transitionSubject.asObservable().pipe(
      take(1),
      timeout(WAIT_TIMEOUT_MS)
    );
  }

  stopWait(): void {
    this.activeDemandId = null;
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
    this.transitionSubject?.complete();
    this.transitionSubject = null;
  }

  private emitTransition(transition: DemandWaitTransition): void {
    if (this.activeDemandId == null || !this.transitionSubject) {
      return;
    }
    this.transitionSubject.next(transition);
    this.transitionSubject.complete();
    this.stopWait();
  }

  private bootstrapFromApi(demandId: number): void {
    const sub = this.clientService.getDemandeById(demandId).subscribe({
      next: (res: any) => {
        const demande = res?.body;
        if (!demande || this.activeDemandId !== demandId) {
          return;
        }
        if (demande.statut === "CANCELED") {
          this.emitTransition({ type: "problem", demandeId: demandId });
          return;
        }
        if (demande.problemReports?.length && demande.statut === "INPROGRESS") {
          this.emitTransition({ type: "problem", demandeId: demandId });
          return;
        }
        if (demande.meetings?.length && demande.meetings[0]?.status === "PENDING") {
          this.emitTransition({
            type: "meeting",
            demandeId: demandId,
            meetings: demande.meetings,
          });
        }
      },
    });
    this.subscriptions.push(sub);
  }

  private listenToRealtime(demandId: number): void {
    const devisSub = this.pusherService.DevisCreatedObservabe().subscribe((res: any) => {
      if (!res?.data?.id) {
        return;
      }
      this.pusherService.resetDevisCreated();
      this.emitTransition({ type: "devis", devisId: Number(res.data.id), demandeId: demandId });
    });
    this.subscriptions.push(devisSub);

    const problemSub = this.pusherService
      .DispatcherSignlerProblemObservable()
      .subscribe((res: any) => {
        const id = res?.data?.problem?.demande?.id ?? res?.demande_id;
        if (Number(id) === demandId) {
          this.emitTransition({ type: "problem", demandeId: demandId });
        }
      });
    this.subscriptions.push(problemSub);

    const cancelSub = this.pusherService
      .DispatcherDemandeCancelledObservable()
      .subscribe((res: any) => {
        if (Number(res?.demande_id) === demandId) {
          this.emitTransition({ type: "problem", demandeId: demandId });
        }
      });
    this.subscriptions.push(cancelSub);

    const pollSub = timer(30_000, 30_000).subscribe(() => {
      if (this.activeDemandId === demandId) {
        this.bootstrapFromApi(demandId);
      }
    });
    this.subscriptions.push(pollSub);
  }
}
